import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles,
} from "@material-ui/core";
import {
  Save,
  CloudUpload,
  DeleteOutline,
  ExpandMore,
  MailOutline,
  Drafts,
  WhatsApp,
  MonetizationOn,
  Tune,
  AvTimer,
  Collections,
  SettingsEthernet,
  Dashboard,
  Business,
  CheckCircle,
  ErrorOutline,
  People,
  Link as LinkIcon,
  LinkOff
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ServerMetrics from "../ServerMetrics";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === "dark";

  return ({
  root: {
    padding: theme.spacing(2),
    width: "100%",
    maxWidth: "100%",
    margin: 0,
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1)
    }
  },
  hero: {
    borderRadius: 16,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    background: isDark
      ? "linear-gradient(135deg, rgba(56,189,248,.12) 0%, rgba(16,185,129,.12) 100%)"
      : "linear-gradient(135deg, rgba(15,76,129,.1) 0%, rgba(19,111,99,.1) 100%)",
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.07)"
  },
  title: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 700
  },
  subtitle: {
    fontSize: "0.86rem",
    color: theme.palette.text.secondary
  },
  tabs: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    "& .MuiTabs-flexContainer": {
      padding: theme.spacing(0.5),
      flexWrap: "nowrap"
    },
    "& .MuiTabs-scroller": {
      overflowX: "auto !important",
      WebkitOverflowScrolling: "touch"
    },
    "& .MuiTab-root": {
      textTransform: "none",
      minHeight: 40,
      borderRadius: 8,
      fontWeight: 600
    }
  },
  tabIcon: {
    color: theme.palette.text.secondary,
    fontSize: 18
  },
  sectionTitle: {
    marginBottom: theme.spacing(0.75),
    fontWeight: 700
  },
  sectionDescription: {
    marginBottom: theme.spacing(2),
    fontSize: "0.8rem",
    color: theme.palette.text.secondary
  },
  form: {
    marginTop: theme.spacing(1)
  },
  sectionCard: {
    borderRadius: 14,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    background: theme.palette.background.paper
  },
  sectionCardScrollable: {
    maxHeight: "calc(100vh - 280px)",
    overflowY: "auto",
    paddingRight: theme.spacing(1.2),
    [theme.breakpoints.down("sm")]: {
      maxHeight: "none",
      overflowY: "visible",
      paddingRight: theme.spacing(2)
    }
  },
  textField: {
    marginBottom: theme.spacing(2)
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "flex-end"
  },
  actionsSticky: {
    position: "fixed",
    right: theme.spacing(3),
    bottom: theme.spacing(2),
    zIndex: 1200,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.16)",
    [theme.breakpoints.down("sm")]: {
      right: theme.spacing(1.5),
      left: theme.spacing(1.5),
      bottom: theme.spacing(1.5),
      justifyContent: "flex-end"
    }
  },
  button: {
    minWidth: 160
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4)
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    marginTop: -theme.spacing(0.3),
    marginBottom: theme.spacing(2)
  },
  brandingPreviewImg: {
    maxWidth: "100%",
    maxHeight: 120,
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    objectFit: "contain",
    background: "#fafafa"
  },
  uploadButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  uploadActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  smtpRowCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: isDark ? "rgba(255,255,255,.04)" : "rgba(248,250,252,.9)",
    marginBottom: theme.spacing(2)
  },
  smtpColumnCard: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    padding: theme.spacing(1.5),
    height: "100%"
  },
  compactField: {
    marginBottom: theme.spacing(1.2)
  },
  templatePreview: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: isDark ? "rgba(255,255,255,.03)" : "#f8fafc",
    padding: theme.spacing(1.25),
    maxHeight: 140,
    overflow: "auto"
  },
  accordion: {
    borderRadius: "10px !important",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    marginBottom: theme.spacing(1.25),
    "&:before": {
      display: "none"
    }
  },
  accordionSummary: {
    minHeight: 50,
    "& .MuiAccordionSummary-content": {
      margin: "12px 0"
    }
  },
  accordionSummaryContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.1)
  },
  accordionSummaryIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiSvgIcon-root": {
      fontSize: 18
    }
  },
  iconSmtp: {
    color: "#0f4c81",
    background: "linear-gradient(135deg, rgba(15,76,129,.16) 0%, rgba(56,189,248,.16) 100%)"
  },
  iconWelcomeEmail: {
    color: "#9a3412",
    background: "linear-gradient(135deg, rgba(249,115,22,.16) 0%, rgba(251,191,36,.16) 100%)"
  },
  iconWelcomeWhatsapp: {
    color: "#166534",
    background: "linear-gradient(135deg, rgba(34,197,94,.16) 0%, rgba(16,185,129,.16) 100%)"
  },
  iconTrial: {
    color: "#4338ca",
    background: "linear-gradient(135deg, rgba(99,102,241,.16) 0%, rgba(129,140,248,.16) 100%)"
  },
  accordionDetails: {
    display: "block",
    paddingTop: 0
  },
  iosSwitchRoot: {
    width: 36,
    height: 22,
    padding: 0,
    margin: "0 8px 0 0"
  },
  iosSwitchBase: {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#34c759",
        borderColor: "#34c759",
        opacity: 1
      }
    }
  },
  iosSwitchThumb: {
    width: 18,
    height: 18,
    boxShadow: "0 1px 3px rgba(0,0,0,0.25)"
  },
  iosSwitchTrack: {
    borderRadius: 11,
    border: `1px solid ${theme.palette.type === "light" ? "#d1d5db" : "#475569"}`,
    backgroundColor: theme.palette.type === "light" ? "#e5e7eb" : "#334155",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"])
  },
  switchFieldLabel: {
    marginLeft: 0,
    marginRight: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.6),
    "&:last-child": {
      marginBottom: 0
    },
    "& .MuiFormControlLabel-label": {
      marginLeft: theme.spacing(0.75),
      fontSize: "0.88rem"
    }
  },
  statsCard: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    background: theme.palette.background.paper,
    height: "100%"
  },
  statsIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: isDark ? "rgba(59,130,246,.2)" : "rgba(59,130,246,.12)",
    color: "#1d4ed8"
  },
  statsValue: {
    fontWeight: 700,
    fontSize: "1.2rem",
    lineHeight: 1.15
  },
  statsLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary
  }
});
});

// helper pra montar URL da imagem (relativa ou absoluta)
const resolveImageUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http")) return value;

  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return value;

  const normalizedBase = base.replace(/\/+$/, "");
  const path = value.startsWith("/") ? value : `/${value}`;
  return `${normalizedBase}${path}`;
};

const GlobalConfig = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingWelcomeTest, setSendingWelcomeTest] = useState(false);
  const [sendingBillingEmailTest, setSendingBillingEmailTest] = useState(false);
  const [sendingBillingWhatsappTest, setSendingBillingWhatsappTest] = useState(false);
  const [welcomeTestEmail, setWelcomeTestEmail] = useState("");
  const [billingTestEmail, setBillingTestEmail] = useState("");
  const [billingTestPhone, setBillingTestPhone] = useState("");
  const [uploading, setUploading] = useState({
    loginLogo: false,
    loginBackground: false
  });
  const [removing, setRemoving] = useState({
    loginLogo: false,
    loginBackground: false
  });

  const [config, setConfig] = useState({
    mpAccessToken: "",
    paymentGateway: "mercadopago",
    asaasApiKey: "",
    asaasWebhookSecret: "",
    smtpHost: "",
    smtpPort: "",
    smtpSecure: "false",
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
    welcomeEmailEnabled: "disabled",
    welcomeWhatsappEnabled: "enabled",
    welcomeEmailSubject: "Bem-vindo(a) | Seu acesso foi liberado",
    welcomeEmailTemplate:
      "<div style='font-family:Arial,Helvetica,sans-serif;background:#f4f6fb;padding:24px'><table role='presentation' width='100%' cellspacing='0' cellpadding='0' style='max-width:620px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e5e7eb'><tr><td style='background:linear-gradient(135deg,#0f4c81,#136f63);padding:22px 24px;color:#ffffff'><h1 style='margin:0;font-size:20px'>Bem-vindo(a), {name}!</h1><p style='margin:8px 0 0;font-size:13px;opacity:.92'>Seu acesso foi liberado com sucesso.</p></td></tr><tr><td style='padding:24px'><p style='margin:0 0 14px;color:#1f2937;font-size:14px'>Olá, {name}. Abaixo estão os dados iniciais da sua conta:</p><table role='presentation' width='100%' cellspacing='0' cellpadding='0' style='background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px'><tr><td style='padding:14px 16px;color:#111827;font-size:14px;line-height:1.6'><strong>Empresa:</strong> {companyName}<br/><strong>E-mail:</strong> {email}<br/><strong>Senha provisória:</strong> {password}<br/><strong>Vencimento:</strong> {dueDate}</td></tr></table><p style='margin:16px 0 0;color:#374151;font-size:14px'>Acesse: <a href='{loginUrl}' style='color:#0f4c81;text-decoration:none'>{loginUrl}</a></p><p style='margin:18px 0 0;color:#6b7280;font-size:12px'>Por segurança, recomendamos alterar sua senha no primeiro acesso.</p></td></tr><tr><td style='padding:14px 24px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:11px'>Este é um e-mail automático da plataforma {companyName}.</td></tr></table></div>",
    trialExpiration: "",
    loginLogo: "",
    loginBackground: "",
    loginWhatsapp: "",
    wuzapiBaseUrl: "",
    wuzapiAdminToken: "",
    billingDueEmailEnabled: "disabled",
    billingDueWhatsappEnabled: "disabled",
    billingDueDaysBefore: "3",
    billingDueEmailSubject: "Lembrete: vencimento em {daysToDue} dia(s) - {companyName}",
    billingDueEmailTemplate:
      "<p>Olá, {companyName}.</p><p>Seu vencimento será em <strong>{daysToDue} dia(s)</strong> ({dueDate}).</p><p>Valor atual: <strong>{invoiceValue}</strong>.</p><p>Link da fatura: <a href='{invoiceLink}'>{invoiceLink}</a></p>",
    billingDueWhatsappTemplate:
      "Olá, {companyName}! Lembrete: sua fatura vence em {daysToDue} dia(s), na data {dueDate}. Valor: {invoiceValue}. Link: {invoiceLink}",
    channelWhatsappBaileysEnabled: "enabled",
    channelWhatsappWuzapiEnabled: "enabled",
    channelWhatsappOfficialEnabled: "enabled",
    channelFacebookEnabled: "enabled",
    channelInstagramEnabled: "enabled"
  });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    expiredCompanies: 0,
    totalUsers: 0,
    onlineUsers: 0,
    totalConnections: 0,
    activeConnections: 0,
    disconnectedConnections: 0
  });

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === "trialExpiration" || name === "billingDueDaysBefore") {
      const onlyDigits = value.replace(/\D/g, "");
      return setConfig(prev => ({ ...prev, [name]: onlyDigits }));
    }

    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleWelcomeEmailToggle = (event) => {
    setConfig(prev => ({
      ...prev,
      welcomeEmailEnabled: event.target.checked ? "enabled" : "disabled"
    }));
  };

  const handleWelcomeWhatsappToggle = (event) => {
    setConfig(prev => ({
      ...prev,
      welcomeWhatsappEnabled: event.target.checked ? "enabled" : "disabled"
    }));
  };

  const handleBillingToggle = (name) => (event) => {
    setConfig(prev => ({
      ...prev,
      [name]: event.target.checked ? "enabled" : "disabled"
    }));
  };

  const handleChannelToggle = (name) => (event) => {
    setConfig(prev => ({
      ...prev,
      [name]: event.target.checked ? "enabled" : "disabled"
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const selectedGateway = String(config.paymentGateway || "").toLowerCase();

    if (selectedGateway === "asaas") {
      if (!String(config.asaasApiKey || "").trim()) {
        toast.error("Informe a API Key do Asaas.");
        return;
      }

      if (!String(config.asaasWebhookSecret || "").trim()) {
        toast.error("Informe o Token de Webhook do Asaas.");
        return;
      }
    }

    setSaving(true);

    try {
      await api.put("/global-config", config);
      setConfig(prev => ({
        ...prev
      }));
      toast.success("Configurações salvas com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/global-config");
      setConfig(prev => ({
        ...prev,
        ...data,
        trialExpiration:
          data.trialExpiration !== undefined && data.trialExpiration !== null
            ? String(data.trialExpiration)
            : prev.trialExpiration
      }));
      setWelcomeTestEmail(data?.smtpUser || "");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.super === false) {
      history.replace("/");
    }
  }, [user, history]);

  useEffect(() => {
    if (user?.super === false) return;
    fetchConfig();
  }, [user]);

  useEffect(() => {
    if (user?.super === false) return;
    if (tab !== 0) return;

    let mounted = true;
    const loadDashboard = async () => {
      setDashboardLoading(true);
      try {
        const { data } = await api.get("/global-config/dashboard-summary");
        if (mounted && data) {
          setDashboardStats(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        if (mounted) {
          toastError(err);
        }
      } finally {
        if (mounted) setDashboardLoading(false);
      }
    };

    loadDashboard();
    return () => {
      mounted = false;
    };
  }, [tab, user]);

  useEffect(() => {
    if (!billingTestEmail && user?.email) {
      setBillingTestEmail(String(user.email));
    }
    if (!billingTestPhone && user?.company?.phone) {
      setBillingTestPhone(String(user.company.phone));
    }
  }, [user, billingTestEmail, billingTestPhone]);

  if (user?.super === false) {
    return null;
  }

  const handleBrandingUpload = async (field, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("field", field); // "loginLogo" ou "loginBackground"

    try {
      setUploading(prev => ({ ...prev, [field]: true }));
      const { data } = await api.post("/global-config/upload", formData);

      // backend retorna { field, url }
      const url = data?.url || data?.[field];

      if (url) {
        setConfig(prev => ({
          ...prev,
          [field]: url
        }));
      }

      toast.success("Imagem atualizada com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleBrandingRemove = async (field) => {
    try {
      setRemoving(prev => ({ ...prev, [field]: true }));
      await api.post("/global-config/upload/remove", { field });

      setConfig(prev => ({
        ...prev,
        [field]: ""
      }));

      toast.success("Imagem removida com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setRemoving(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSendWelcomeEmailTest = async () => {
    if (!welcomeTestEmail) {
      toast.error("Informe um e-mail para teste.");
      return;
    }

    setSendingWelcomeTest(true);
    try {
      await api.post("/global-config/test-welcome-email", {
        testEmail: welcomeTestEmail,
        smtpHost: config.smtpHost,
        smtpPort: config.smtpPort,
        smtpSecure: config.smtpSecure,
        smtpUser: config.smtpUser,
        smtpPass: config.smtpPass,
        smtpFrom: config.smtpFrom,
        welcomeEmailEnabled: config.welcomeEmailEnabled,
        welcomeEmailSubject: config.welcomeEmailSubject,
        welcomeEmailTemplate: config.welcomeEmailTemplate
      });
      toast.success("E-mail de teste enviado com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSendingWelcomeTest(false);
    }
  };

  const handleSendBillingDueEmailTest = async () => {
    if (!String(billingTestEmail).trim()) {
      toast.error("Informe um e-mail para teste.");
      return;
    }

    setSendingBillingEmailTest(true);
    try {
      await api.post("/global-config/test-billing-due-email", {
        testEmail: String(billingTestEmail).trim(),
        smtpHost: config.smtpHost,
        smtpPort: config.smtpPort,
        smtpSecure: config.smtpSecure,
        smtpUser: config.smtpUser,
        smtpPass: config.smtpPass,
        smtpFrom: config.smtpFrom,
        billingDueEmailSubject: config.billingDueEmailSubject,
        billingDueEmailTemplate: config.billingDueEmailTemplate
      });
      toast.success("E-mail de teste de vencimento enviado com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSendingBillingEmailTest(false);
    }
  };

  const handleSendBillingDueWhatsappTest = async () => {
    if (!String(billingTestPhone).trim()) {
      toast.error("Informe um telefone para teste.");
      return;
    }

    setSendingBillingWhatsappTest(true);
    try {
      await api.post("/global-config/test-billing-due-whatsapp", {
        testPhone: String(billingTestPhone).trim(),
        billingDueWhatsappTemplate: config.billingDueWhatsappTemplate
      });
      toast.success("WhatsApp de teste de vencimento enviado com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSendingBillingWhatsappTest(false);
    }
  };

  const formId = "global-config-form";

  const renderSaveButton = (extraActions = null, sticky = false) => (
    <div className={`${classes.actions} ${sticky ? classes.actionsSticky : ""}`}>
      {extraActions}
      <Button
        type="submit"
        color="primary"
        variant="contained"
        className={classes.button}
        startIcon={!saving && <Save />}
        disabled={saving}
      >
        {saving ? <CircularProgress size={20} /> : "Salvar"}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingWrapper}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab icon={<Dashboard className={classes.tabIcon} />} label="Dashboard" />
        <Tab icon={<MonetizationOn className={classes.tabIcon} />} label="Meios de Pagamento" />
        <Tab icon={<Tune className={classes.tabIcon} />} label="Configurações" />
        <Tab icon={<Collections className={classes.tabIcon} />} label="Login / Capa" />
        <Tab icon={<SettingsEthernet className={classes.tabIcon} />} label="WuzAPI" />
      </Tabs>

      <form id={formId} onSubmit={handleSubmit} className={classes.form}>
        {/* ABA 0: DASHBOARD */}
        {tab === 0 && (
          <Paper elevation={0} className={classes.sectionCard}>
            {dashboardLoading ? (
              <div className={classes.loadingWrapper}>
                <CircularProgress size={24} />
              </div>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><Business fontSize="small" /></span><div><Typography className={classes.statsLabel}>Empresas cadastradas</Typography><Typography className={classes.statsValue}>{dashboardStats.totalCompanies}</Typography></div></div></Grid>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><CheckCircle fontSize="small" /></span><div><Typography className={classes.statsLabel}>Empresas Ativas</Typography><Typography className={classes.statsValue}>{dashboardStats.activeCompanies}</Typography></div></div></Grid>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><ErrorOutline fontSize="small" /></span><div><Typography className={classes.statsLabel}>Empresas Vencidas</Typography><Typography className={classes.statsValue}>{dashboardStats.expiredCompanies}</Typography></div></div></Grid>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><People fontSize="small" /></span><div><Typography className={classes.statsLabel}>Total de Usuários</Typography><Typography className={classes.statsValue}>{dashboardStats.totalUsers}</Typography></div></div></Grid>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><People fontSize="small" /></span><div><Typography className={classes.statsLabel}>Usuários Online</Typography><Typography className={classes.statsValue}>{dashboardStats.onlineUsers}</Typography></div></div></Grid>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><LinkIcon fontSize="small" /></span><div><Typography className={classes.statsLabel}>Total de Conexões</Typography><Typography className={classes.statsValue}>{dashboardStats.totalConnections}</Typography></div></div></Grid>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><LinkIcon fontSize="small" /></span><div><Typography className={classes.statsLabel}>Conexões Ativas</Typography><Typography className={classes.statsValue}>{dashboardStats.activeConnections}</Typography></div></div></Grid>
                <Grid item xs={12} sm={6} md={3}><div className={classes.statsCard}><span className={classes.statsIconWrap}><LinkOff fontSize="small" /></span><div><Typography className={classes.statsLabel}>Conexões Desconectadas</Typography><Typography className={classes.statsValue}>{dashboardStats.disconnectedConnections}</Typography></div></div></Grid>
              </Grid>
            )}

            <Box mt={3}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Dados do Servidor
              </Typography>
              <ServerMetrics embedded hideAccessCheck />
            </Box>
          </Paper>
        )}

        {/* ABA 1: MEIOS DE PAGAMENTO */}
        {tab === 1 && (
          <Paper elevation={0} className={classes.sectionCard}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Pagamentos
            </Typography>
            <Typography className={classes.sectionDescription}>
              Selecione o gateway padrão da plataforma e configure as credenciais.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Gateway padrão"
                  name="paymentGateway"
                  value={config.paymentGateway || "mercadopago"}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                >
                  <MenuItem value="mercadopago">Mercado Pago</MenuItem>
                  <MenuItem value="asaas">Asaas</MenuItem>
                </TextField>
                <div className={classes.helperText}>
                  Define qual gateway será usado no botão de pagamento das faturas.
                </div>
              </Grid>
              {String(config.paymentGateway || "").toLowerCase() === "mercadopago" && (
                <Grid item xs={12}>
                  <TextField
                    label="Mercado Pago - Access Token"
                    name="mpAccessToken"
                    value={config.mpAccessToken}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    className={classes.textField}
                    size="small"
                  />
                  <div className={classes.helperText}>
                    Use o Access Token do Mercado Pago da conta principal da
                    plataforma. As empresas clientes usarão sempre essa
                    configuração.
                  </div>
                </Grid>
              )}

              {String(config.paymentGateway || "").toLowerCase() === "asaas" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label="Asaas - API Key"
                      name="asaasApiKey"
                      value={config.asaasApiKey}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      size="small"
                    />
                    <div className={classes.helperText}>
                      API Key do Asaas usada para criar cobranças.
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Asaas - Token de Webhook"
                      name="asaasWebhookSecret"
                      value={config.asaasWebhookSecret}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      className={classes.textField}
                      size="small"
                    />
                    <div className={classes.helperText}>
                      URL do webhook Asaas: <strong>{`${process.env.REACT_APP_BACKEND_URL || ""}/subscription/webhook/asaas`}</strong>
                    </div>
                  </Grid>
                </>
              )}
            </Grid>
            {renderSaveButton(null, true)}
          </Paper>
        )}

        {/* ABA 2: SMTP */}
        {tab === 2 && (
          <Paper elevation={0} className={`${classes.sectionCard} ${classes.sectionCardScrollable}`}>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                <div className={classes.accordionSummaryContent}>
                  <span className={`${classes.accordionSummaryIcon} ${classes.iconWelcomeWhatsapp}`}>
                    <Tune />
                  </span>
                  <Typography variant="subtitle2">Canais Disponíveis</Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Paper elevation={0} className={classes.smtpRowCard}>
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.channelWhatsappBaileysEnabled) === "enabled"}
                        onChange={handleChannelToggle("channelWhatsappBaileysEnabled")}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="WhatsApp (Baileys)"
                  />
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.channelWhatsappWuzapiEnabled) === "enabled"}
                        onChange={handleChannelToggle("channelWhatsappWuzapiEnabled")}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="WhatsApp (wuzAPI)"
                  />
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.channelWhatsappOfficialEnabled) === "enabled"}
                        onChange={handleChannelToggle("channelWhatsappOfficialEnabled")}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="WhatsApp (API Oficial)"
                  />
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.channelFacebookEnabled) === "enabled"}
                        onChange={handleChannelToggle("channelFacebookEnabled")}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="Facebook"
                  />
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.channelInstagramEnabled) === "enabled"}
                        onChange={handleChannelToggle("channelInstagramEnabled")}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="Instagram"
                  />
                </Paper>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                <div className={classes.accordionSummaryContent}>
                  <span className={`${classes.accordionSummaryIcon} ${classes.iconTrial}`}>
                    <AvTimer />
                  </span>
                  <Typography variant="subtitle2">Trial / Assinatura</Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <TextField
                  label="Dias de teste"
                  name="trialExpiration"
                  value={config.trialExpiration}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                />
                <div className={classes.helperText} style={{ marginBottom: 0 }}>
                  Quantidade de dias de teste que a empresa nova terá. Se vazio,
                  o sistema usa o valor padrão do .env (APP_TRIALEXPIRATION, ex.: 3).
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                <div className={classes.accordionSummaryContent}>
                  <span className={`${classes.accordionSummaryIcon} ${classes.iconSmtp}`}>
                    <MailOutline />
                  </span>
                  <Typography variant="subtitle2">E-mail (SMTP e Recuperação de Senha)</Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <TextField
                  label="Host"
                  name="smtpHost"
                  value={config.smtpHost}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      label="Porta"
                      name="smtpPort"
                      value={config.smtpPort}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      className={classes.compactField}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Secure"
                      name="smtpSecure"
                      value={config.smtpSecure}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      className={classes.compactField}
                      size="small"
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="E-mail"
                  name="smtpUser"
                  value={config.smtpUser}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                />
                <TextField
                  label="Senha de app"
                  name="smtpPass"
                  value={config.smtpPass}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                  type="password"
                />
                <TextField
                  label="Remetente (FROM)"
                  name="smtpFrom"
                  value={config.smtpFrom}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                />
                <div className={classes.helperText} style={{ marginBottom: 0 }}>
                  Use `true` em Secure para SSL/TLS e `false` para STARTTLS.
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                <div className={classes.accordionSummaryContent}>
                  <span className={`${classes.accordionSummaryIcon} ${classes.iconWelcomeEmail}`}>
                    <Drafts />
                  </span>
                  <Typography variant="subtitle2">E-mail de Boas-vindas</Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Paper elevation={0} className={classes.smtpRowCard}>
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.welcomeEmailEnabled) === "enabled"}
                        onChange={handleWelcomeEmailToggle}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="Enviar e-mail de boas-vindas automaticamente"
                  />
                  <div className={classes.helperText} style={{ marginBottom: 0 }}>
                    Ative para disparar e-mail no cadastro de empresas e usuários.
                  </div>
                </Paper>

                <TextField
                  label="Assunto do e-mail de boas-vindas"
                  name="welcomeEmailSubject"
                  value={config.welcomeEmailSubject}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                  disabled={String(config.welcomeEmailEnabled) !== "enabled"}
                />

                <TextField
                  label="Conteúdo HTML do e-mail"
                  name="welcomeEmailTemplate"
                  value={config.welcomeEmailTemplate}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  multiline
                  minRows={5}
                  disabled={String(config.welcomeEmailEnabled) !== "enabled"}
                />
                <div className={classes.helperText}>
                  Variáveis: {"{name}, {companyName}, {email}, {password}, {dueDate}, {loginUrl}"}.
                </div>

                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                  Pré-visualização
                </Typography>
                <Box
                  className={classes.templatePreview}
                  dangerouslySetInnerHTML={{
                    __html: config.welcomeEmailTemplate || "<p>Template vazio</p>"
                  }}
                />

                <Grid container spacing={1} style={{ marginTop: 8 }}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="E-mail para teste"
                      value={welcomeTestEmail}
                      onChange={(e) => setWelcomeTestEmail(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      style={{ height: 40 }}
                      onClick={handleSendWelcomeEmailTest}
                      disabled={sendingWelcomeTest}
                    >
                      {sendingWelcomeTest ? "Enviando..." : "Enviar teste"}
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                <div className={classes.accordionSummaryContent}>
                  <span className={`${classes.accordionSummaryIcon} ${classes.iconWelcomeWhatsapp}`}>
                    <WhatsApp />
                  </span>
                  <Typography variant="subtitle2">WhatsApp de Boas-vindas</Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Paper elevation={0} className={classes.smtpRowCard}>
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.welcomeWhatsappEnabled) === "enabled"}
                        onChange={handleWelcomeWhatsappToggle}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="Enviar WhatsApp de boas-vindas automaticamente"
                  />
                  <div className={classes.helperText} style={{ marginBottom: 0 }}>
                    Ative para enviar WhatsApp de boas-vindas no cadastro de empresas (signup).
                  </div>
                </Paper>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                <div className={classes.accordionSummaryContent}>
                  <span className={`${classes.accordionSummaryIcon} ${classes.iconWelcomeEmail}`}>
                    <MonetizationOn />
                  </span>
                  <Typography variant="subtitle2">Aviso de Vencimento - Faturas</Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Paper elevation={0} className={classes.smtpRowCard}>
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.billingDueEmailEnabled) === "enabled"}
                        onChange={handleBillingToggle("billingDueEmailEnabled")}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="Enviar aviso de vencimento por e-mail"
                  />
                  <FormControlLabel
                    className={classes.switchFieldLabel}
                    control={
                      <Switch
                        checked={String(config.billingDueWhatsappEnabled) === "enabled"}
                        onChange={handleBillingToggle("billingDueWhatsappEnabled")}
                        color="primary"
                        disableRipple
                        classes={{
                          root: classes.iosSwitchRoot,
                          switchBase: classes.iosSwitchBase,
                          thumb: classes.iosSwitchThumb,
                          track: classes.iosSwitchTrack
                        }}
                      />
                    }
                    label="Enviar aviso de vencimento por WhatsApp"
                  />
                </Paper>
                <TextField
                  label="Dias antes do vencimento"
                  name="billingDueDaysBefore"
                  value={config.billingDueDaysBefore}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Assunto do e-mail"
                  name="billingDueEmailSubject"
                  value={config.billingDueEmailSubject}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                />
                <TextField
                  label="Template do e-mail (HTML)"
                  name="billingDueEmailTemplate"
                  value={config.billingDueEmailTemplate}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  multiline
                  minRows={4}
                />
                <TextField
                  label="Template do WhatsApp"
                  name="billingDueWhatsappTemplate"
                  value={config.billingDueWhatsappTemplate}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  multiline
                  minRows={3}
                />
                <TextField
                  label="E-mail para teste"
                  value={billingTestEmail}
                  onChange={(e) => setBillingTestEmail(e.target.value)}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                />
                <TextField
                  label="WhatsApp para teste (com DDD)"
                  value={billingTestPhone}
                  onChange={(e) => setBillingTestPhone(e.target.value)}
                  variant="outlined"
                  fullWidth
                  className={classes.compactField}
                  size="small"
                  placeholder="11999998888"
                />
                <div className={classes.helperText}>
                  Variáveis: {"{companyName}, {dueDate}, {daysToDue}, {invoiceValue}, {invoiceLink}"}.
                </div>
                <div className={classes.actions} style={{ marginTop: 8 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    onClick={handleSendBillingDueEmailTest}
                    disabled={sendingBillingEmailTest || sendingBillingWhatsappTest || saving}
                    style={{ marginRight: 8 }}
                  >
                    {sendingBillingEmailTest ? "Enviando e-mail..." : "Testar envio e-mail"}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    onClick={handleSendBillingDueWhatsappTest}
                    disabled={sendingBillingWhatsappTest || sendingBillingEmailTest || saving}
                    style={{ marginRight: 8 }}
                  >
                    {sendingBillingWhatsappTest ? "Enviando WhatsApp..." : "Testar envio WhatsApp"}
                  </Button>
                </div>
              </AccordionDetails>
            </Accordion>
          </Paper>
        )}

        {/* ABA 3: LOGIN / CAPA */}
        {tab === 3 && (
          <Paper elevation={0} className={classes.sectionCard}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Login / capa
            </Typography>
            <Typography className={classes.sectionDescription}>
              Personalize logo, capa de login e link de suporte.
            </Typography>

            <Grid container spacing={3}>
              {/* LOGO DO LOGIN (apenas upload + preview) */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Logo do login</Typography>

                {config.loginLogo && (
                  <Box mt={1} mb={1}>
                    <img
                      src={resolveImageUrl(config.loginLogo)}
                      alt="Logo do login"
                      className={classes.brandingPreviewImg}
                    />
                  </Box>
                )}

                <input
                  id="loginLogoUpload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    handleBrandingUpload("loginLogo", e.target.files[0])
                  }
                />
                <div className={classes.uploadActions}>
                  <label htmlFor="loginLogoUpload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      className={classes.uploadButton}
                      disabled={uploading.loginLogo}
                    >
                      {uploading.loginLogo ? "Enviando..." : "Enviar logo"}
                    </Button>
                  </label>

                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DeleteOutline />}
                    className={classes.uploadButton}
                    disabled={!config.loginLogo || removing.loginLogo}
                    onClick={() => handleBrandingRemove("loginLogo")}
                  >
                    {removing.loginLogo ? "Removendo..." : "Remover"}
                  </Button>
                </div>

                <div className={classes.helperText}>
                  Se nenhuma imagem for enviada, o sistema usa <code>/logo.png</code>.
                </div>
              </Grid>

              {/* CAPA / BACKGROUND DO LOGIN (apenas upload + preview) */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">
                  Imagem de fundo (capa do login)
                </Typography>

                {config.loginBackground && (
                  <Box mt={1} mb={1}>
                    <img
                      src={resolveImageUrl(config.loginBackground)}
                      alt="Capa do login"
                      className={classes.brandingPreviewImg}
                    />
                  </Box>
                )}

                <input
                  id="loginBackgroundUpload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    handleBrandingUpload("loginBackground", e.target.files[0])
                  }
                />
                <div className={classes.uploadActions}>
                  <label htmlFor="loginBackgroundUpload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      className={classes.uploadButton}
                      disabled={uploading.loginBackground}
                    >
                      {uploading.loginBackground ? "Enviando..." : "Enviar capa"}
                    </Button>
                  </label>

                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DeleteOutline />}
                    className={classes.uploadButton}
                    disabled={!config.loginBackground || removing.loginBackground}
                    onClick={() => handleBrandingRemove("loginBackground")}
                  >
                    {removing.loginBackground ? "Removendo..." : "Remover"}
                  </Button>
                </div>

                <div className={classes.helperText}>
                  Recomendada imagem em <code>.webp</code> ou <code>.jpg</code>. 
                  Se vazio, o sistema usa a capa padrão.
                </div>
              </Grid>

              {/* LINK DO WHATSAPP DO LOGIN (mantém campo de texto) */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Link do WhatsApp do login"
                  name="loginWhatsapp"
                  value={config.loginWhatsapp}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                />
                <div className={classes.helperText}>
                  Exemplo: <code>https://wa.me/5541999999999</code>. Usado no botão
                  de WhatsApp da tela de login e no botão "Chamar suporte".
                </div>
              </Grid>
            </Grid>
            {renderSaveButton()}
          </Paper>
        )}

        {/* ABA 4: WUZAPI */}
        {tab === 4 && (
          <Paper elevation={0} className={classes.sectionCard}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              WuzAPI
            </Typography>
            <Typography className={classes.sectionDescription}>
              Configure os dados finais retornados pela instalação do WuzAPI na VPS.
              Essas informações ficam centralizadas no Super Admin para uso global.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="URL WuzAPI"
                  name="wuzapiBaseUrl"
                  value={config.wuzapiBaseUrl}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="http://127.0.0.1:8080"
                />
                <div className={classes.helperText}>
                  URL base do serviço WuzAPI desse servidor.
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Admin Token"
                  name="wuzapiAdminToken"
                  value={config.wuzapiAdminToken}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  type="password"
                />
                <div className={classes.helperText}>
                  Token administrativo para criar usuários/sessões no WuzAPI.
                </div>
              </Grid>

            </Grid>
            {renderSaveButton()}
          </Paper>
        )}
      </form>

      {tab === 2 && (
        <div className={`${classes.actions} ${classes.actionsSticky}`}>
          <Button
            type="submit"
            form={formId}
            color="primary"
            variant="contained"
            className={classes.button}
            startIcon={!saving && <Save />}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GlobalConfig;
